import { useEffect, useRef, useCallback } from 'react';
import { usePlayerStoreApi } from '../store/PlayerStore';

export interface PlayerStoreSyncOptions {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  playerRef: React.RefObject<any>;
  getLiveStatus: () => {
    isLive: boolean;
    isNearEdge: boolean;
    liveEdge?: number;
  };
  skipForward: () => void;
  skipBack: () => void;
  setQuality: (height: number) => void;
  getAvailableQualities: () => Array<{ height: number; bandwidth: number; label: string }>;
  seekToLiveEdge: () => void;
}

/**
 * Hook to sync Player state with the PlayerStore when wrapped in a PlayerProvider.
 *
 * This hook handles:
 * - Registering video element and Shaka player with the store
 * - Registering player methods (skip, quality, live controls)
 * - Syncing playback state (time, play/pause, volume, etc.)
 * - Syncing live stream state
 *
 * @returns Object with store instance and callbacks for additional sync operations
 */
export function usePlayerStoreSync({
  videoRef,
  playerRef,
  getLiveStatus,
  skipForward,
  skipBack,
  setQuality,
  getAvailableQualities,
  seekToLiveEdge,
}: PlayerStoreSyncOptions) {
  const store = usePlayerStoreApi();

  // Track registered player to avoid redundant updates
  const registeredPlayerRef = useRef<any>(null);

  // Register video element with store
  useEffect(() => {
    if (!store) return;

    const video = videoRef.current;
    if (video) {
      store.getState()._registerVideoElement(video);
    }

    return () => {
      store.getState()._registerVideoElement(null);
    };
  }, [store, videoRef]);

  // Register Shaka player with store
  useEffect(() => {
    if (!store) return;

    const player = playerRef.current;
    // Only register if player changed
    if (player !== registeredPlayerRef.current) {
      registeredPlayerRef.current = player;
      store.getState()._registerShakaPlayer(player);
    }

    return () => {
      store.getState()._registerShakaPlayer(null);
      registeredPlayerRef.current = null;
    };
  }, [store, playerRef]);

  // Wrapper for getLiveStatus that matches store's expected type
  const getLiveStatusForStore = useCallback(() => {
    const status = getLiveStatus();
    return {
      isLive: status.isLive,
      isNearEdge: status.isNearEdge,
      liveEdge: status.liveEdge,
    };
  }, [getLiveStatus]);

  // Register methods with store
  useEffect(() => {
    if (!store) return;

    store.getState()._registerMethods({
      skipForward,
      skipBack,
      setQuality,
      getAvailableQualities,
      seekToLiveEdge,
      getLiveStatus: getLiveStatusForStore,
    });
  }, [store, skipForward, skipBack, setQuality, getAvailableQualities, seekToLiveEdge, getLiveStatusForStore]);

  // Sync video playback state to store
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !store) return;

    const updatePlaybackState = () => {
      store.getState()._updateState({
        currentTime: video.currentTime,
        duration: video.duration || 0,
        isPlaying: !video.paused && !video.ended,
        isPaused: video.paused,
        isEnded: video.ended,
        isMuted: video.muted,
        volume: video.volume,
        isLoading: video.readyState < 3, // HAVE_FUTURE_DATA
      });
    };

    const onPlay = () => {
      store.getState()._updateState({ isPlaying: true, isPaused: false, isEnded: false });
    };

    const onPause = () => {
      store.getState()._updateState({ isPlaying: false, isPaused: true });
    };

    const onEnded = () => {
      store.getState()._updateState({ isPlaying: false, isPaused: true, isEnded: true });
    };

    const onTimeUpdate = () => {
      const liveStatus = getLiveStatus();
      store.getState()._updateState({
        currentTime: video.currentTime,
        isLive: liveStatus.isLive,
        isNearLiveEdge: liveStatus.isNearEdge,
        liveEdge: liveStatus.liveEdge,
        timeBehindLive: liveStatus.liveEdge ? liveStatus.liveEdge - video.currentTime : undefined,
      });
    };

    const onDurationChange = () => {
      store.getState()._updateState({ duration: video.duration || 0 });
    };

    const onVolumeChange = () => {
      store.getState()._updateState({ isMuted: video.muted, volume: video.volume });
    };

    const onLoadStart = () => {
      store.getState()._updateState({ isLoading: true });
    };

    const onCanPlay = () => {
      store.getState()._updateState({ isLoading: false });
    };

    const onError = () => {
      store.getState()._updateState({ error: new Error(video.error?.message || 'Video error') });
    };

    // Add event listeners
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onEnded);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('durationchange', onDurationChange);
    video.addEventListener('volumechange', onVolumeChange);
    video.addEventListener('loadstart', onLoadStart);
    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('error', onError);

    // Initial state update
    updatePlaybackState();

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('durationchange', onDurationChange);
      video.removeEventListener('volumechange', onVolumeChange);
      video.removeEventListener('loadstart', onLoadStart);
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('error', onError);
    };
  }, [store, videoRef, getLiveStatus]);

  // Callback for syncing quality changes to store
  const syncQualityChange = useCallback((quality: { height: number; bandwidth: number }) => {
    if (store) {
      store.getState()._updateState({ currentQuality: quality });
    }
  }, [store]);

  // Callback for updating available qualities in store
  const updateAvailableQualities = useCallback(() => {
    if (store) {
      store.getState()._updateState({ availableQualities: getAvailableQualities() });
    }
  }, [store, getAvailableQualities]);

  return {
    store,
    syncQualityChange,
    updateAvailableQualities,
  };
}
